import { Link, NavLink, useLocation, type Location } from 'react-router';
import NavIcon from './NavIcon';
import React from 'react';
import { useSidebarNavContext } from './SidebarNavProvider';
import { useWindowSize } from 'react-use';
import LockIcon from '../DesignSystems/LockIcon';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { shouldCloseSidebarOnNavigate } from './sidebarNavClose';

/**
 * Keeps a row visible while the pointer lands on the wrapper, not a disabled
 * control.
 */
function HoverLock({
  tooltip,
  children,
}: {
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={<span className="tw:block tw:w-full tw:cursor-not-allowed" />}
        >
          <span className="tw:pointer-events-none tw:block tw:opacity-60">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const rowClass = (secondary?: boolean) =>
  cn(
    'tw:h-8 tw:w-full tw:justify-start tw:gap-2 tw:px-3 tw:font-normal tw:shadow-none',
    'tw:text-foreground tw:no-underline tw:hover:no-underline tw:hover:text-accent-foreground',
    'tw:visited:text-foreground tw:visited:no-underline',
    'tw:aria-[current=page]:bg-accent tw:aria-[current=page]:text-accent-foreground',
    secondary && 'tw:pl-8'
  );

type ListGroupNavItemProps = {
  to: string;
  icon?: string;
  iconNode?: React.ReactNode;
  label: string;
  secondary?: boolean;
  end?: boolean;
  isLocked?: boolean;
  isDisabled?: boolean;
  checkWidth?: boolean;
  onClick?: () => void;
  showArrow?: boolean;
  isActive?: boolean | ((location: Location) => boolean);
  /**
   * When set, the row stays visible, does not navigate, and shows this on
   * hover.
   */
  lockTooltip?: string;
};

export const ListGroupNavItem: React.FC<ListGroupNavItemProps> = ({
  to,
  icon,
  iconNode,
  label,
  secondary,
  end,
  isLocked,
  isDisabled,
  checkWidth,
  onClick,
  showArrow = false,
  isActive,
  lockTooltip,
}) => {
  const { close, handleShowUpgradeModal, stuck } = useSidebarNavContext();
  const { width } = useWindowSize();
  const location = useLocation();
  const customActive =
    typeof isActive === 'function' ? isActive(location) : isActive;

  const leadingIcon = secondary ? null : iconNode != null ? (
    iconNode
  ) : icon ? (
    <NavIcon icon={icon} />
  ) : null;

  if (lockTooltip) {
    return (
      <HoverLock tooltip={lockTooltip}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={rowClass(secondary)}
          aria-disabled
          tabIndex={-1}
        >
          {leadingIcon}
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left">
            {label}
          </span>
          {showArrow ? (
            <i className="material-symbols-outlined tw:ms-auto tw:text-base">
              arrow_right
            </i>
          ) : null}
        </Button>
      </HoverLock>
    );
  }

  if (isLocked) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={rowClass(secondary)}
        onClick={handleShowUpgradeModal}
      >
        {leadingIcon}
        <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left">
          {label}
        </span>
        <LockIcon />
      </Button>
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    // Stick keeps the hamburger open across page navigations.
    const maybeClose = () => {
      if (shouldCloseSidebarOnNavigate(stuck)) close();
    };
    if (checkWidth) {
      if (width < 430) {
        maybeClose();
      } else if (onClick) {
        onClick();
      }
    } else if (onClick) {
      onClick();
    } else {
      maybeClose();
    }
  };

  const linkAriaCurrent =
    customActive === undefined ? undefined : customActive ? 'page' : undefined;

  return (
    <Button
      variant="ghost"
      size="sm"
      nativeButton={false}
      disabled={isDisabled}
      className={rowClass(secondary)}
      render={
        isActive != null ? (
          <Link
            to={to}
            aria-disabled={isDisabled || undefined}
            aria-current={linkAriaCurrent}
            onClick={handleClick}
          />
        ) : (
          <NavLink
            to={to}
            end={end}
            aria-disabled={isDisabled || undefined}
            onClick={handleClick}
          />
        )
      }
    >
      {leadingIcon}
      <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left">
        {label}
      </span>
      {showArrow ? (
        <i className="material-symbols-outlined tw:ms-auto tw:text-base">
          arrow_right
        </i>
      ) : null}
    </Button>
  );
};

type ListGroupButtonItemProps = {
  onClick: () => void;
  icon: string;
  label: string;
  isLocked?: boolean;
  isDisabled?: boolean;
  open?: boolean;
  /**
   * When set, the row stays visible, does not open a panel, and shows this on
   * hover.
   */
  lockTooltip?: string;
};

export const ListGroupButtonItem: React.FC<ListGroupButtonItemProps> = ({
  onClick,
  icon,
  label,
  isLocked,
  isDisabled,
  open,
  lockTooltip,
}) => {
  const { handleShowUpgradeModal } = useSidebarNavContext();
  const button = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={isDisabled}
      className={rowClass()}
      onClick={
        lockTooltip ? undefined : isLocked ? handleShowUpgradeModal : onClick
      }
    >
      <NavIcon icon={icon} />
      <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-left">
        {label}
      </span>
      <i className="material-symbols-outlined tw:text-base">
        {open ? 'arrow_drop_up' : 'arrow_right'}
      </i>
      {isLocked ? <LockIcon /> : null}
    </Button>
  );
  if (!lockTooltip) return button;
  return <HoverLock tooltip={lockTooltip}>{button}</HoverLock>;
};

export function SidebarNavSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-2">
      <div className="tw:px-3 tw:pt-3 tw:text-xs tw:font-medium tw:text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

export function SidebarNavBack({
  title,
  onBack,
  icon,
}: {
  title: string;
  onBack: () => void;
  icon?: string;
}) {
  return (
    <div className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:pt-4 tw:pb-2">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Back"
        onClick={onBack}
      >
        <i className="material-symbols-outlined tw:text-base">arrow_back</i>
      </Button>
      {icon ? (
        <i className="material-symbols-outlined tw:text-base">{icon}</i>
      ) : null}
      <div className="tw:text-sm tw:font-medium">{title}</div>
    </div>
  );
}
